import {
  CommonHelperFunctions,
  ESdChangeReason,
  ESymbolCategory,
  IAction,
  IActionParameter,
  IDesign,
  IDisplayItem,
  IScreenDesign,
  ISymbol,
  SpecialDIIds
} from "@kuika/kuika-cl-model";
import _ from "lodash";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import KuikaSelectbox, {
  KuikaSelectboxOption,
  KuikaSelectboxOptionGroup
} from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import { IActionPanelStatesCanBeUpdatedFromChild } from "./action-panel";
import ActionParameterTitle from "./action-parameter-title";
import { SymbolPicker } from "./symbol-picker";

export interface INodeTreeExportSymbolsGroupProps {
  action: IAction;
  parameters: IActionParameter[];
  updateValues: (value: IActionPanelStatesCanBeUpdatedFromChild) => void;
  changeActionParameter: (parameter: IActionParameter, symbol: ISymbol, changeReason?: ESdChangeReason) => void;
}

class NodeTreeExportSymbolsGroup extends PureSdComponent<
  INodeTreeExportSymbolsGroupProps & IWithSDProps & IWithContextProp,
  {}
> {
  handleComponentSelect = (value: any, allDi: IDisplayItem[]) => {
    let fixedValue = allDi[value]?.id;
    const params = _.cloneDeep(this.props.parameters);
    const _parameter = this.props.parameters.find((element) => element.name === "nodeTreeName")!;
    if (_parameter === undefined) {
      return;
    }
    if (!_parameter.symbol) _parameter.symbol = {} as any;
    _parameter.symbol.fixedValue = fixedValue;
    _parameter.symbol.category = ESymbolCategory.fixed;
    const valuesToUpdate = {
      actionParameters: params
    } as IActionPanelStatesCanBeUpdatedFromChild;
    valuesToUpdate.actionParameters?.push(_parameter);
    this.props.updateValues(valuesToUpdate);
  };

  getSelectValue = (allDi: IDisplayItem[]) => {
    let selectValue;
    const _parameter = this.props.parameters.find((element) => element.name === "nodeTreeName")!;
    if (_parameter && _parameter.symbol) {
      const { fixedValue } = _parameter.symbol;
      allDi.forEach((di, i) => {
        if (di.id.toString() === fixedValue.toString()) {
          selectValue = i;
        }
      });
    }
    return selectValue;
  };

  getAllDisplayItemsOfDi = (di: IDisplayItem, result: IDisplayItem[]) => {
    if (di.children)
      for (let i = 0; i < di.children.length; i++) {
        if (!di.children[i].friendlyName) {
          di.children[i].friendlyName = di.children[i].id.toString();
        }
        result.push(di.children[i]);
        this.getAllDisplayItemsOfDi(di.children[i], result);
      }
  };

  fillAllDisplayItemsRecursive = (sd: IDesign, result: IDisplayItem[]) => {
    if (sd.displayItems)
      for (let i = 0; i < sd.displayItems.length; i++) {
        if (!sd.displayItems[i].friendlyName) {
          sd.displayItems[i].friendlyName = sd.displayItems[i].id.toString();
        }
        result.push(sd.displayItems[i]);
        this.getAllDisplayItemsOfDi(sd.displayItems[i], result);
      }
  };

  renderComponentList = (): JSX.Element => {
    const sd: IScreenDesign = this.props.sd.getScreenDesign();
    let allDi: IDisplayItem[] = [];

    this.fillAllDisplayItemsRecursive(sd, allDi);

    const rootDI = allDi.find((di) => di.id.toString() === SpecialDIIds.rootDI.toString() && di.children.length > 0);
    if (rootDI) {
      rootDI.friendlyName = "Screen";
    }

    allDi.sort((a, b) => CommonHelperFunctions.compareObjectWithField(a, b, "friendlyName"));

    allDi = allDi.filter((di) => {
      if (di?.item?.name) {
        return di.item.name.toLowerCase() === "nodetree";
      }
    });

    // A component is picked by its place in this sorted list rather than by its id, and
    // handleComponentSelect reads the picked index back out of the same list.
    const componentGroups: KuikaSelectboxOptionGroup[] =
      allDi.length > 0
        ? [
            {
              label: "Screen",
              options: allDi.map((di: IDisplayItem, index: number): KuikaSelectboxOption => ({
                key: di.id.toString(),
                value: index,
                label: di.friendlyName
              }))
            }
          ]
        : [];

    return (
      <>
        <div style={{ marginTop: 12 }} className="kuika_add_actions_properties_name">
          Select a Node Tree
        </div>
        <div className="kuika_property-grid-column">
          <KuikaSelectbox
            searchable
            size="sm"
            style={{ width: "100%" }}
            value={this.getSelectValue(allDi) ?? null}
            onChange={(val: number): void => {
              this.handleComponentSelect(val, allDi);
            }}
            className="kuika_add_actions_inputs_size"
            groups={componentGroups}
          />
        </div>
      </>
    );
  };

  render() {
    return (
      <div style={{ width: "100%" }}>
        {this.renderComponentList()}
        {this.props.parameters
          ?.filter((parameter) =>
            parameter.isOptionalParameter === true
              ? parameter.symbol.fixedValue
                ? true
                : parameter.isExternalVisible === true
              : true
          )
          .map((parameter, id) => {
            return (
              parameter.name !== "nodeTreeName" && (
                <div key={id}>
                  <span style={{ marginBottom: 6, marginTop: 8 }} className="k_Regular-G1-Left kuika_rightPanelDIName">
                    <ActionParameterTitle parameter={parameter} />
                  </span>
                  {
                    <SymbolPicker
                      action={this.props.action}
                      changeType="onBlur"
                      symbol={parameter.symbol}
                      valueType={parameter.Typename}
                      targetCardinality={parameter.cardinality}
                      onSelectedSymbol={(symbol: ISymbol, changeReason?: ESdChangeReason) =>
                        this.props.changeActionParameter(parameter, symbol, changeReason)
                      }
                      bindingParameter={parameter}
                    ></SymbolPicker>
                  }
                </div>
              )
            );
          })}
      </div>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.DiActionUpdated ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.WFStepMappingChange
    ) {
      return true;
    }
    return false;
  };
}

const nodeTreeExportSymbolsGroup = withContext<INodeTreeExportSymbolsGroupProps>(
  withSD<INodeTreeExportSymbolsGroupProps>(NodeTreeExportSymbolsGroup)
);
export { nodeTreeExportSymbolsGroup as NodeTreeExportSymbolsGroup };
